import { createFileRoute } from "@tanstack/react-router";
import { DashboardLayout } from "@/projects/components/dashboard-layout";
import { useTeachers, useSubmissions } from "@/projects/lib/useStore";
import { upsertTeacher, toggleTeacherActive, type Teacher } from "@/projects/lib/store";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/projects/components/ui/table";
import { Button } from "@/projects/components/ui/button";
import { Input } from "@/projects/components/ui/input";
import { Label } from "@/projects/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/projects/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/projects/components/ui/alert-dialog";
import { useState } from "react";
import { UserPlus, KeyRound, Edit, Power } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/projects/lib/utils";

export const Route = createFileRoute("/admin/teachers")({
  head: () => ({ meta: [{ title: "Teacher Management — Admin" }] }),
  component: TeachersPage,
});

function TeachersPage() {
  const teachers = useTeachers();
  const submissions = useSubmissions();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Teacher | null>(null);
  const [confirmToggle, setConfirmToggle] = useState<Teacher | null>(null);

  const rows = teachers.map((t) => {
    const mine = submissions.filter((s) => s.assignedTeacherId === t.id);
    return {
      ...t,
      total: mine.length,
      pending: mine.filter((s) => s.status === "Assigned for Verification").length,
      reward: mine.filter((s) => s.status === "Ready for Reward").length,
      notProper: mine.filter((s) => s.status === "Not Proper").length,
    };
  });

  return (
    <DashboardLayout role="admin" title="Teacher Management" breadcrumb={["Home", "Teachers"]}>
      <div className="mb-4 flex items-center justify-between">
        <div className="text-sm text-muted-foreground">{teachers.length} teachers</div>
        <Button onClick={() => { setEditing(null); setOpen(true); }}>
          <UserPlus className="mr-2 h-4 w-4" /> Add Teacher
        </Button>
      </div>

      <div className="overflow-hidden rounded-xl border bg-card shadow-soft">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/50">
                <TableHead>Teacher</TableHead>
                <TableHead>Subject</TableHead>
                <TableHead>Contact</TableHead>
                <TableHead className="text-center">Assigned</TableHead>
                <TableHead className="text-center">Pending</TableHead>
                <TableHead className="text-center">Reward</TableHead>
                <TableHead className="text-center">Not Proper</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((t) => (
                <TableRow key={t.id}>
                  <TableCell>
                    <div className="font-medium">{t.name}</div>
                    <div className="text-xs text-muted-foreground">{t.id}</div>
                  </TableCell>
                  <TableCell>{t.subject}</TableCell>
                  <TableCell className="text-xs">
                    <div>{t.email}</div>
                    <div className="text-muted-foreground">{t.mobile}</div>
                  </TableCell>
                  <TableCell className="text-center font-semibold">{t.total}</TableCell>
                  <TableCell className="text-center text-royal">{t.pending}</TableCell>
                  <TableCell className="text-center text-success">{t.reward}</TableCell>
                  <TableCell className="text-center text-destructive">{t.notProper}</TableCell>
                  <TableCell>
                    <span className={cn(
                      "inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-xs font-medium",
                      t.active ? "border-success/30 bg-success/10 text-success" : "border-muted-foreground/30 bg-muted text-muted-foreground",
                    )}>
                      <span className={cn("h-1.5 w-1.5 rounded-full", t.active ? "bg-success" : "bg-muted-foreground")} />
                      {t.active ? "Active" : "Inactive"}
                    </span>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button size="sm" variant="ghost" onClick={() => { setEditing(t); setOpen(true); }}>
                        <Edit className="h-3.5 w-3.5" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => toast.success("Password reset link sent")}>
                        <KeyRound className="h-3.5 w-3.5" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setConfirmToggle(t)}>
                        <Power className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      <TeacherFormDialog open={open} onOpenChange={setOpen} teacher={editing} />

      <AlertDialog open={!!confirmToggle} onOpenChange={(o) => !o && setConfirmToggle(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{confirmToggle?.active ? "Deactivate" : "Activate"} teacher?</AlertDialogTitle>
            <AlertDialogDescription>
              {confirmToggle?.active
                ? `${confirmToggle?.name} will no longer be able to log in or receive new assignments.`
                : `${confirmToggle?.name} will regain access to the teacher dashboard.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => {
              if (confirmToggle) {
                toggleTeacherActive(confirmToggle.id);
                toast.success(`Teacher ${confirmToggle.active ? "deactivated" : "activated"}`);
              }
              setConfirmToggle(null);
            }}>Confirm</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardLayout>
  );
}

function TeacherFormDialog({ open, onOpenChange, teacher }: { open: boolean; onOpenChange: (v: boolean) => void; teacher: Teacher | null }) {
  const [f, setF] = useState<Teacher>(
    teacher ?? { id: "", name: "", email: "", mobile: "", subject: "", username: "", active: true },
  );
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!f.name || !f.id || !f.email || !f.subject || !f.username) {
      toast.error("Please fill all required fields");
      return;
    }
    if (!teacher && password !== confirm) {
      toast.error("Passwords do not match");
      return;
    }
    upsertTeacher(f);
    toast.success(teacher ? "Teacher updated" : "Teacher added successfully");
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{teacher ? "Edit Teacher" : "Add Teacher"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="grid gap-3 sm:grid-cols-2">
          <Field label="Teacher Name *"><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></Field>
          <Field label="Teacher ID *"><Input value={f.id} onChange={(e) => setF({ ...f, id: e.target.value })} disabled={!!teacher} /></Field>
          <Field label="Email *"><Input type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></Field>
          <Field label="Mobile"><Input value={f.mobile} onChange={(e) => setF({ ...f, mobile: e.target.value })} /></Field>
          <Field label="Subject / Department *"><Input value={f.subject} onChange={(e) => setF({ ...f, subject: e.target.value })} /></Field>
          <Field label="Username *"><Input value={f.username} onChange={(e) => setF({ ...f, username: e.target.value })} /></Field>
          {!teacher && (
            <>
              <Field label="Password *"><Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} /></Field>
              <Field label="Confirm Password *"><Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} /></Field>
            </>
          )}
          <DialogFooter className="sm:col-span-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit">{teacher ? "Save Changes" : "Add Teacher"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return <div><Label className="text-xs">{label}</Label>{children}</div>;
}
